import React from 'react'
import cc from 'classcat'
import { LoadingSpinner } from 'components/Loading'

export type Type =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'link'
  | 'danger'
  | 'info'

export interface Props {
  children: any
  Component?: 'a' | 'button'
  $type?: Type
  $fluid?: boolean
  $inverted?: boolean
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  className?: string
  form?: string
  type?: string
  to?: string
  $size: 'sm' | 'md'
  $roundedSquare?: boolean
  $shadow?: boolean
}

const Button = ({
  Component,
  children,
  $type,
  $fluid,
  $inverted,
  className,
  disabled,
  loading,
  $size,
  $roundedSquare,
  $shadow,
  ...rest
}: Props) => {
  const Render = Component as any
  const classMap: {
    [key: string]: {
      normal: {
        enabled: string
        disabled: string
      }
      inverted: {
        enabled: string
        disabled: string
      }
    }
  } = {
    primary: {
      normal: {
        enabled: 'text-white bg-green',
        disabled: 'text-white bg-gray-600',
      },
      inverted: {
        enabled: 'text-green bg-white',
        disabled: 'text-green bg-gray-300 opacity-75',
      },
    },
    secondary: {
      normal: {
        enabled: 'bg-transparent text-green border-2 border-green',
        disabled: 'bg-transparent text-gray-600 border-2 border-gray-600',
      },
      inverted: {
        enabled: 'bg-transparent text-white border-2 border-white',
        disabled:
          'bg-transparent text-gray-300 border-2 border-gray-300 opacity-75',
      },
    },
    tertiary: {
      normal: {
        enabled: 'bg-transparent text-green',
        disabled: 'bg-transparent text-gray-600',
      },
      inverted: {
        enabled: 'bg-transparent text-white',
        disabled: 'text-gray-300 text-opacity-75',
      },
    },
    danger: {
      normal: {
        enabled: 'bg-red-500 text-white',
        disabled: 'text-red-600 bg-gray-400',
      },
      inverted: {
        enabled: 'bg-white text-red-500',
        disabled: 'bg-white text-red-500 opacity-75',
      },
    },
    info: {
      normal: {
        enabled: 'bg-primary text-white',
        disabled: 'text-gray-700 bg-gray-500',
      },
      inverted: {
        enabled: 'bg-white text-primary',
        disabled: 'bg-white text-primary opacity-75',
      },
    },
    link: {
      normal: {
        enabled: 'text-gray-700',
        disabled: 'text-gray-600',
      },
      inverted: {
        enabled: 'text-white',
        disabled: 'text-white text-opacity-75',
      },
    },
  }

  return (
    <Render
      className={cc([
        'inline-block heading-3 tracking-widest leading-none',
        {
          'w-full': $fluid,
          'p-2': !$roundedSquare && !loading && $size === 'sm',
          'p-5': !$roundedSquare && !loading && $size === 'md',
          'px-2 py-2': !$roundedSquare && loading && $size === 'sm',
          'px-5 py-4': !$roundedSquare && loading && $size === 'md',
          'w-14 h-14 rounded-full p-1 text-center': $roundedSquare,
          'shadow-lg': $shadow,
        },
        $type &&
          classMap[$type][$inverted ? 'inverted' : 'normal'][
            disabled ? 'disabled' : 'enabled'
          ],

        // custom classes
        className,
      ])}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <LoadingSpinner $size={$size === 'sm' ? 'xs' : 'sm'} />
      ) : (
        children
      )}
    </Render>
  )
}

Button.defaultProps = {
  Component: 'button',
  $type: 'primary',
  $size: 'md',
}

export default Button
